import { Injectable } from "@angular/core";
import { computed } from "@angular/core";
import {
  signalStore,
  withState,
  withComputed,
  withMethods,
  patchState,
} from "@ngrx/signals";
import { RoleTypes, ADMIN_ROLES } from "../../guards/roletypes";
import { StudentProfileType } from "../../api/models/student-profile-type";

/**
 * Capability flags derived from the current user's role + profileType.
 *
 * Templates/registries SHOULD check capabilities, not raw role strings.
 * Adding a new capability: declare it in the type, derive it in
 * `CurrentUserStore`'s withComputed block.
 */
export interface Capabilities {
  isStudent: boolean;
  isCompany: boolean;
  isTeacher: boolean;
  isAdmin: boolean;
  isProfessional: boolean;
  /** Follow-up days (Meeloopdagen) only visible to non-Professional students. */
  canViewFollowUpDays: boolean;
  /** Admin / professional-admin can manage profile tags. */
  canManageTags: boolean;
  /** Students can maintain a blacklist of companies on their profile. */
  canManageBlacklist: boolean;
}

export interface CurrentUserState {
  roles: string[];
  profileType: StudentProfileType | null;
}

const initialState: CurrentUserState = {
  roles: [],
  profileType: null,
};

@Injectable({ providedIn: "root" })
export class CurrentUserStore extends signalStore(
  withState(initialState),

  withComputed((state) => {
    const isStudent = computed(() => state.roles().includes(RoleTypes.Student));
    const isCompany = computed(() => state.roles().includes(RoleTypes.Company));
    const isTeacher = computed(() => state.roles().includes(RoleTypes.Teacher));
    const isAdmin = computed(() =>
      state.roles().some((r) => ADMIN_ROLES.includes(r))
    );
    /** Full admin only — for capabilities the limited admin roles shouldn't get. */
    const isFullAdmin = computed(() =>
      state.roles().includes(RoleTypes.Beheerder)
    );
    const isProfessional = computed(
      () => state.profileType() === StudentProfileType.Professional
    );

    const capabilities = computed<Capabilities>(() => ({
      isStudent: isStudent(),
      isCompany: isCompany(),
      isTeacher: isTeacher(),
      isAdmin: isAdmin(),
      isProfessional: isProfessional(),
      canViewFollowUpDays: isStudent() && !isProfessional(),
      canManageTags: isAdmin(),
      canManageBlacklist: isStudent(),
    }));

    return {
      isStudent,
      isCompany,
      isTeacher,
      isAdmin,
      isFullAdmin,
      isProfessional,
      capabilities,
    };
  }),

  withMethods((store) => ({
    setRoles: (roles: string[]) => {
      patchState(store, { roles: roles ?? [] });
    },
    setProfileType: (profileType: StudentProfileType | null) => {
      patchState(store, { profileType });
    },
    reset: () => {
      patchState(store, initialState);
    },
  }))
) {}
